import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTrackVisibility } from "@/hooks/use-track-visibility";
import { useColumnResize } from "@/hooks/use-column-resize";
import posthog from "@/lib/posthog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/enhanced-button";
import { ResizeHandle } from "@/components/ui/resize-handle";
import { TruncatedCell } from "@/components/ui/TruncatedCell";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { X, ArrowUpDown, ArrowUp, ArrowDown, Filter, ChevronLeft, ChevronRight, Bell, Bot, CreditCard, ChevronDown, Check, Download, MessageSquare } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { exportInvoicesCsv } from "@/services/export.service";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { DateFilterKey, resolveDateFilter, DATE_FILTER_OPTIONS } from "@/utils/dateFilters";
import { DateFilter } from "@/components/ui/DateFilter";
import { formatDate, formatAmount, formatAmountCompact } from "@/utils/expenseFormatters";
import { Invoice, CustomerPayment, InvoicePagination, InvoiceApiResponse } from "@blinkbooks/types";
import apiClient from "@/utils/httpClient";
import { fetchReceivablesKPIs, fetchAgedReceivablesKPIs, fetchBusinessKPIs } from "@/services/kpi.service";
import { ReceivablesKPIs, AgedReceivablesKPIs, BusinessOverviewKPIs } from "@blinkbooks/types";
import { KpiGroupCard } from "@/components/ui/KpiGroupCard";
import { useToast } from "@/hooks/use-toast";
import { ReminderCreateModal } from "@/components/ReminderCreateModal";
import CommentsSheet, { CommentsSheetTarget } from "@/components/CommentsSheet";
import { useOrganization } from "@/contexts/OrganizationContext";
import { UserOrganizationRole } from "@blinkbooks/types";

interface RowReminder {
  text: string;
  targetNames: string[];
}

const StatusBadge = ({ status }: { status: string }) => {
  const variant = {
    paid: "default",
    unpaid: "secondary",
    partially_paid: "secondary",
    void: "outline",
  }[status] || "default";

  const colorClass = {
    paid: "bg-green-100 text-green-800",
    unpaid: "bg-orange-100 text-orange-800",
    partially_paid: "bg-blue-100 text-blue-800",
    void: "bg-gray-100 text-gray-800",
  }[status] || "";

  return (
    <Badge variant={variant as "default" | "secondary" | "destructive" | "outline"} className={colorClass}>
      {status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
    </Badge>
  );
};

// Payment detail sheet
const PaymentDetailSheet = ({
  payment,
  open,
  onClose,
}: {
  payment: CustomerPayment | null;
  open: boolean;
  onClose: () => void;
}) => (
  <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
    <SheetContent side="right" className="w-full max-w-md overflow-y-auto">
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Payment Details
        </SheetTitle>
      </SheetHeader>
      {payment && (
        <div className="mt-6 space-y-4">
          <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
            <Detail label="Customer" value={payment.customerName} />
            <Detail label="Payment Date" value={formatDate(payment.date)} />
            <Detail label="Amount" value={formatAmount(payment.totalAmount, payment.currency)} />
            <Detail label="Method" value={payment.method || "—"} />
            <Detail label="Reference #" value={payment.referenceNumber || "—"} />
            {payment.createdAt && <Detail label="Created" value={formatDate(payment.createdAt)} />}
          </div>
        </div>
      )}
    </SheetContent>
  </Sheet>
);

const Detail = ({ label, value }: { label: string; value: string }) => (
  <div className="flex justify-between text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium text-right max-w-[60%] break-words">{value}</span>
  </div>
);

// Cache for customer payments by invoice ID
const paymentsCache = new Map<string, CustomerPayment[]>();

const PaymentDetails = ({
  invoiceId,
  status,
  onPaymentClick,
}: {
  invoiceId: string;
  status: string;
  onPaymentClick: (p: CustomerPayment) => void;
}) => {
  const [payments, setPayments] = useState<CustomerPayment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPayments = async () => {
      if (status === 'unpaid') {
        setPayments([]);
        setLoading(false);
        return;
      }
      const cached = paymentsCache.get(invoiceId);
      if (cached !== undefined) {
        setPayments(cached);
        setLoading(false);
        return;
      }
      try {
        const invoicePayments = await apiClient.get<CustomerPayment[]>(`/customer-payments/by-invoice?invoiceId=${invoiceId}`);
        const result = invoicePayments || [];
        paymentsCache.set(invoiceId, result);
        setPayments(result);
      } catch {
        setPayments([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPayments();
  }, [invoiceId, status]);

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Loading payments…</div>;
  if (payments.length === 0) return <div className="p-4 text-sm text-muted-foreground">No payments for this invoice.</div>;

  return (
    <div className="p-4 bg-muted/5 border-t">
      <h4 className="text-sm font-semibold mb-3">Invoice Payments</h4>
      <div className="space-y-2">
        {payments.map((payment) => (
          <div
            key={payment.id}
            onClick={() => onPaymentClick(payment)}
            className="grid grid-cols-4 gap-3 text-sm p-3 bg-background rounded border cursor-pointer hover:bg-muted/20 transition-colors"
          >
            <div>
              <span className="text-muted-foreground text-xs block">Payment Date</span>
              {formatDate(payment.date)}
            </div>
            <div>
              <span className="text-muted-foreground text-xs block">Amount</span>
              {formatAmount(payment.totalAmount, payment.currency)}
            </div>
            <div>
              <span className="text-muted-foreground text-xs block">Method</span>
              {payment.method || '—'}
            </div>
            <div>
              <span className="text-muted-foreground text-xs block">Ref #</span>
              {payment.referenceNumber || '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

interface ActionsProps {
  invoice: Invoice;
  onAskChat: (msg: string) => void;
  onReminder: () => void;
  onComment: () => void;
  rowReminder: RowReminder | undefined;
  commentCount: number | undefined;
}

const ActionsCell = ({ invoice, onAskChat, onReminder, onComment, rowReminder, commentCount }: ActionsProps) => {
  const { activeViewId } = useOrganization();
  const buildContext = () => {
    const lines = [
      `[Invoice Context]`,
      `Invoice #: ${invoice.invoiceNumber || invoice.id}`,
      `Customer: ${invoice.customerName}`,
      `Issue Date: ${formatDate(invoice.issueDate)}`,
      `Due Date: ${formatDate(invoice.dueDate)}`,
      `Amount: ${formatAmount(invoice.totalAmount, invoice.currency)}`,
      `Balance Due: ${formatAmount(invoice.balance, invoice.currency)}`,
      `Status: ${invoice.status.replace(/_/g, ' ')}`,
    ];
    return lines.join('\n');
  };

  return (
    <div className="flex items-center gap-1 h-full">
      {!activeViewId && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onReminder()}
          className="h-7 w-7 p-0 hover:bg-orange-100 transition-opacity"
          title="Set reminder"
        >
          <Bell className="h-3.5 w-3.5 text-orange-600" />
        </Button>
      )}
      {!activeViewId && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onComment()}
          className="h-7 w-7 p-0 hover:bg-blue-100 transition-opacity relative"
          title="Comments"
        >
          <MessageSquare className="h-3.5 w-3.5 text-blue-600" />
          {!!commentCount && (
            <span className="absolute -top-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-blue-500 text-[9px] font-bold text-white flex items-center justify-center leading-none">
              {commentCount > 9 ? '9+' : commentCount}
            </span>
          )}
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onAskChat(buildContext())}
        className="h-7 w-7 p-0 hover:bg-violet-100 transition-opacity"
        title="Ask AI about this invoice"
      >
        <Bot className="h-3.5 w-3.5 text-violet-600" />
      </Button>
      {rowReminder && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="h-2 w-2 rounded-full bg-orange-400 flex-shrink-0 ml-1 cursor-default" />
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-xs text-xs space-y-1">
            <p className="font-semibold">Reminder set</p>
            <p>{rowReminder.text}</p>
            <p className="text-muted-foreground">→ {rowReminder.targetNames.join(', ')}</p>
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};

type SortField = 'invoiceNumber' | 'customer' | 'issueDate' | 'dueDate' | 'status' | 'amount' | 'balanceDue';
type SortDirection = 'asc' | 'desc' | null;

const SALES_COL_WIDTHS = [110, 160, 100, 100, 100, 120, 100, 120, 100];
const SALES_COL_WIDTHS_WITH_COMPANY = [110, 140, 160, 100, 100, 100, 120, 100, 120, 100];

interface SalesReceivablesProps {
  onAskChat: (message: string) => void;
  dateFilterKey: DateFilterKey | '';
  dateCustomFrom?: Date;
  dateCustomTo?: Date;
  onDateFilterChange: (key: DateFilterKey | '', from?: Date, to?: Date) => void;
  currentUserId?: string;
  role?: UserOrganizationRole | null;
}

const SalesReceivables = ({ onAskChat, dateFilterKey, dateCustomFrom, dateCustomTo, onDateFilterChange, currentUserId, role }: SalesReceivablesProps) => {
  const visibilityRef = useTrackVisibility("sales_receivables_table");
  const { activeViewId } = useOrganization();
  const colWidths = activeViewId ? SALES_COL_WIDTHS_WITH_COMPANY : SALES_COL_WIDTHS;
  const { gridTemplate, totalWidth, startResize } = useColumnResize(colWidths);
  const companyColOffset = activeViewId ? 1 : 0;
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<CustomerPayment | null>(null);
  const [paymentSheetOpen, setPaymentSheetOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>('dueDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [isLoadingData, setIsLoadingData] = useState(false);

  // Reminder state
  const [reminderModal, setReminderModal] = useState<{ invoiceId: string; label: string } | null>(null);
  const [rowReminders, setRowReminders] = useState<Map<string, RowReminder>>(new Map());
  const [commentsTarget, setCommentsTarget] = useState<CommentsSheetTarget | null>(null);
  const [commentCounts, setCommentCounts] = useState<Map<string, number>>(new Map());

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<InvoicePagination>({
    page: 1, limit: 25, total: 0, totalPages: 0,
  });

  const { toast } = useToast();

  const [serverKpis, setServerKpis] = useState<ReceivablesKPIs | null>(null);
  const [serverAgedAR, setServerAgedAR] = useState<AgedReceivablesKPIs | null>(null);
  const [serverBusiness, setServerBusiness] = useState<BusinessOverviewKPIs | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [amountMin, setAmountMin] = useState<string>('');
  const [amountMax, setAmountMax] = useState<string>('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueSoonOnly, setDueSoonOnly] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState(() => ({
    customers: [] as string[], statuses: [] as string[], companies: [] as string[],
    dateFilterKey,
    dateCustomFrom,
    dateCustomTo,
    amountMin: '', amountMax: '',
    overdueOnly: false,
    dueSoonOnly: false,
  }));

  // Keep applied date filters in sync with global date prop changes
  const appliedFiltersRef = useRef(appliedFilters);
  useEffect(() => { appliedFiltersRef.current = appliedFilters; });

  const fetchServerKpis = useCallback(async (key: typeof dateFilterKey, from?: Date, to?: Date, customers?: string[]) => {
    const range = resolveDateFilter(key, from, to);
    if (!range) return;
    try {
      const [receivables, agedAR, business] = await Promise.allSettled([
        fetchReceivablesKPIs(range, customers),
        fetchAgedReceivablesKPIs(range, customers),
        fetchBusinessKPIs(range, customers),
      ]);
      if (receivables.status === 'fulfilled') setServerKpis(receivables.value);
      if (agedAR.status === 'fulfilled') setServerAgedAR(agedAR.value);
      if (business.status === 'fulfilled') setServerBusiness(business.value);
    } catch { /* leave stale */ }
  }, []);

  const prevDateRef = useRef({ dateFilterKey, dateCustomFrom, dateCustomTo });
  useEffect(() => {
    const prev = prevDateRef.current;
    if (prev.dateFilterKey === dateFilterKey &&
        prev.dateCustomFrom?.getTime() === dateCustomFrom?.getTime() &&
        prev.dateCustomTo?.getTime() === dateCustomTo?.getTime()) return;
    prevDateRef.current = { dateFilterKey, dateCustomFrom, dateCustomTo };
    const newFilters = { ...appliedFiltersRef.current, dateFilterKey, dateCustomFrom, dateCustomTo };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    fetchData(1, newFilters);
    fetchServerKpis(dateFilterKey, dateCustomFrom, dateCustomTo, appliedFiltersRef.current.customers);
  // fetchData/fetchServerKpis intentionally excluded — called with explicit args
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilterKey, dateCustomFrom, dateCustomTo]);

  const hasFilterChanges = useMemo(() => (
    JSON.stringify(selectedCustomers) !== JSON.stringify(appliedFilters.customers) ||
    JSON.stringify(selectedStatuses) !== JSON.stringify(appliedFilters.statuses) ||
    JSON.stringify(selectedCompanies) !== JSON.stringify(appliedFilters.companies) ||
    amountMin !== appliedFilters.amountMin ||
    amountMax !== appliedFilters.amountMax ||
    overdueOnly !== appliedFilters.overdueOnly ||
    dueSoonOnly !== appliedFilters.dueSoonOnly
  ), [selectedCustomers, selectedStatuses, selectedCompanies, amountMin, amountMax, overdueOnly, dueSoonOnly, appliedFilters]);

  const hasActiveFilters = useMemo(() => (
    appliedFilters.customers.length > 0 ||
    appliedFilters.statuses.length > 0 ||
    appliedFilters.companies.length > 0 ||
    appliedFilters.amountMin !== '' ||
    appliedFilters.amountMax !== '' ||
    appliedFilters.overdueOnly ||
    appliedFilters.dueSoonOnly
  ), [appliedFilters]);

  const [allFetchedCustomers, setAllFetchedCustomers] = useState<Set<string>>(new Set());
  const [allFetchedOrganizations, setAllFetchedOrganizations] = useState<Map<string, string>>(new Map());

  const fetchData = useCallback(async (page: number = 1, filters = appliedFilters) => {
    setIsLoadingData(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '25');
      if (filters.customers.length > 0) params.set('customer', filters.customers.join('|'));
      if (filters.companies.length > 0) params.set('organizationId', filters.companies.join(','));
      if (filters.dueSoonOnly) {
        params.set('dueSoonOnly', 'true');
      } else {
        if (filters.statuses.length > 0) params.set('status', filters.statuses.join(','));
        if (filters.overdueOnly) params.set('overdueOnly', 'true');
      }
      const resolved = resolveDateFilter(filters.dateFilterKey, filters.dateCustomFrom, filters.dateCustomTo);
      if (resolved) { params.set('dateFrom', resolved.startDate); params.set('dateTo', resolved.endDate); }
      if (filters.amountMin) params.set('amountMin', filters.amountMin);
      if (filters.amountMax) params.set('amountMax', filters.amountMax);

      const response = await apiClient.getRaw<InvoiceApiResponse>(`/invoices?${params.toString()}`);
      if (response?.success) {
        const data = response.data || [];
        const paginationData = response.pagination || { page: 1, limit: 25, total: data.length, totalPages: 1 };
        setInvoices(data);
        setPagination(paginationData);
        setCurrentPage(paginationData.page);
        setAllFetchedCustomers(prev => {
          const next = new Set(prev);
          data.forEach(inv => { if (inv.customerName && inv.customerName !== 'Unknown Customer') next.add(inv.customerName); });
          return next;
        });
        setAllFetchedOrganizations(prev => {
          const next = new Map(prev);
          data.forEach(inv => { if (inv.organizationId && inv.organizationName) next.set(inv.organizationId, inv.organizationName); });
          return next;
        });
      } else {
        setInvoices([]);
        setPagination({ page: 1, limit: 25, total: 0, totalPages: 0 });
      }
    } catch {
      setInvoices([]);
      setPagination({ page: 1, limit: 25, total: 0, totalPages: 0 });
    } finally {
      setIsLoadingData(false);
    }
  }, [appliedFilters]);

  const uniqueCustomers = useMemo(() =>
    Array.from(allFetchedCustomers).sort().map(name => ({ id: name, name })),
    [allFetchedCustomers]
  );

  const uniqueOrganizations = useMemo(() =>
    Array.from(allFetchedOrganizations.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [allFetchedOrganizations]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(1); fetchServerKpis(dateFilterKey, dateCustomFrom, dateCustomTo); }, []);

  const applyFilters = useCallback((overrides: Partial<typeof appliedFilters> = {}) => {
    const newFilters = {
      ...appliedFilters,
      customers: selectedCustomers,
      statuses: selectedStatuses,
      companies: selectedCompanies,
      amountMin,
      amountMax,
      overdueOnly,
      dueSoonOnly,
      dateFilterKey,
      dateCustomFrom,
      dateCustomTo,
      ...overrides,
    };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
    fetchData(1, newFilters);
    fetchServerKpis(newFilters.dateFilterKey, newFilters.dateCustomFrom, newFilters.dateCustomTo, newFilters.customers);
  }, [appliedFilters, selectedCustomers, selectedStatuses, selectedCompanies, amountMin, amountMax, overdueOnly, dueSoonOnly, dateFilterKey, dateCustomFrom, dateCustomTo, fetchData, fetchServerKpis]);

  const handleCustomerToggle = (name: string) => {
    const next = selectedCustomers.includes(name)
      ? selectedCustomers.filter(c => c !== name)
      : [...selectedCustomers, name];
    setSelectedCustomers(next);
    applyFilters({ customers: next });
  };

  const handleCompanyToggle = (id: string) => {
    const next = selectedCompanies.includes(id)
      ? selectedCompanies.filter(c => c !== id)
      : [...selectedCompanies, id];
    setSelectedCompanies(next);
    applyFilters({ companies: next });
  };

  const handleStatusToggle = (s: string) => {
    const next = selectedStatuses.includes(s)
      ? selectedStatuses.filter(x => x !== s)
      : [...selectedStatuses, s];
    setSelectedStatuses(next);
    setDueSoonOnly(false);
    applyFilters({ statuses: next, dueSoonOnly: false });
  };

  const handleOverdueToggle = (checked: boolean) => {
    setOverdueOnly(checked);
    setDueSoonOnly(false);
    applyFilters({ overdueOnly: checked, dueSoonOnly: false });
  };

  const handleDueSoonToggle = (checked: boolean) => {
    setDueSoonOnly(checked);
    if (checked) setOverdueOnly(false);
    applyFilters({ dueSoonOnly: checked, overdueOnly: false });
  };

  const handleApplyFilters = async () => {
    if (amountMin !== '' && amountMax !== '' && parseFloat(amountMin) > parseFloat(amountMax)) {
      toast({ variant: 'destructive', description: 'Min amount cannot be greater than max amount.' });
      return;
    }
    posthog.capture("table_filtered", {
      table: "sales_receivables",
      has_customer_filter: selectedCustomers.length > 0,
      has_status_filter: selectedStatuses.length > 0,
      has_date_filter: dateFilterKey !== "all_time",
      has_amount_filter: amountMin !== "" || amountMax !== "",
      overdue_only: overdueOnly,
    });
    applyFilters();
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      posthog.capture("table_paginated", { table: "sales_receivables", page });
      setCurrentPage(page);
      fetchData(page);
    }
  };

  const kpis = useMemo(() => {
    // Receivables Overview — QBO-based aged AR takes priority, falls back to local DB aggregates
    const openBalance = serverAgedAR
      ? serverAgedAR.total.amount
      : serverKpis
        ? serverKpis.periodOpenAR.amount
        : invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'void').reduce((sum, inv) => sum + inv.balance, 0);
    const openBalanceCount = serverAgedAR
      ? serverAgedAR.total.count
      : serverKpis
        ? serverKpis.periodOpenAR.count
        : invoices.filter(inv => inv.status !== 'paid' && inv.status !== 'void').length;

    const totalBilled = serverKpis?.totalReceivables.amount ?? 0;
    const totalBilledCount = serverKpis?.totalReceivables.count ?? 0;

    const overdueAmount = serverAgedAR
      ? serverAgedAR.overdue.amount
      : serverKpis
        ? serverKpis.atRisk.amount + serverKpis.stuck.amount
        : 0;
    const overdueCount = serverAgedAR
      ? serverAgedAR.overdue.count
      : serverKpis
        ? serverKpis.atRisk.count + serverKpis.stuck.count
        : 0;

    const dueSoonAmount = serverKpis?.dueSoon.amount ?? 0;
    const dueSoonCount  = serverKpis?.dueSoon.count  ?? 0;

    // Collections Performance — always use server aggregates for accuracy
    const cashCollected = serverKpis?.collected.amount ?? 0;
    const invoicesDue = serverKpis?.invoicesDue.amount ?? 0;
    const collectionRate = invoicesDue > 0 ? (cashCollected / invoicesDue) * 100 : null;

    const avgDaysToCollect = serverBusiness?.avgDaysToPay.value ?? null;
    const avgDaysCount = serverBusiness?.avgDaysToPay.count ?? 0;

    return {
      totalBilled, totalBilledCount,
      openBalance, openBalanceCount,
      overdueAmount, overdueCount,
      dueSoonAmount, dueSoonCount,
      cashCollected, collectionRate,
      avgDaysToCollect, avgDaysCount,
    };
  }, [invoices, serverKpis, serverAgedAR, serverBusiness]);

  const periodLabel = (() => {
    if (!dateFilterKey) return null;
    if (dateFilterKey === 'custom') {
      if (!dateCustomFrom || !dateCustomTo) return 'Custom range';
      return `${format(dateCustomFrom, 'MM/dd/yyyy')} – ${format(dateCustomTo, 'MM/dd/yyyy')}`;
    }
    return DATE_FILTER_OPTIONS.find(o => o.key === dateFilterKey)?.label ?? null;
  })();

  const statuses = ['unpaid', 'partially_paid', 'paid', 'void'];

  const clearFilters = () => {
    posthog.capture("table_filters_cleared", { table: "sales_receivables" });
    setSelectedCustomers([]);
    setSelectedStatuses([]);
    setSelectedCompanies([]);
    setAmountMin('');
    setAmountMax('');
    setOverdueOnly(false);
    setDueSoonOnly(false);
    const cleared = { customers: [], statuses: [], companies: [], dateFilterKey, dateCustomFrom, dateCustomTo, amountMin: '', amountMax: '', overdueOnly: false, dueSoonOnly: false };
    setAppliedFilters(cleared);
    setCurrentPage(1);
    fetchData(1, cleared);
    fetchServerKpis(dateFilterKey, dateCustomFrom, dateCustomTo, []);
  };

  const applyKpiQuickFilter = (statuses: string[], overdueOnly: boolean, isDueSoon = false) => {
    setSelectedStatuses(statuses);
    setOverdueOnly(overdueOnly);
    setDueSoonOnly(isDueSoon);
    applyFilters({ statuses, overdueOnly, dueSoonOnly: isDueSoon });
  };

  const handleExportCsv = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      await exportInvoicesCsv({
        customers: appliedFilters.customers,
        statuses: appliedFilters.statuses,
        overdueOnly: appliedFilters.overdueOnly,
        dateFilterKey: appliedFilters.dateFilterKey,
        dateCustomFrom: appliedFilters.dateCustomFrom,
        dateCustomTo: appliedFilters.dateCustomTo,
        amountMin: appliedFilters.amountMin,
        amountMax: appliedFilters.amountMax,
      });
      posthog.capture("csv_exported", { table: "sales_receivables" });
    } catch {
      toast({ variant: 'destructive', description: 'Failed to export CSV. Please try again.' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleSort = (field: SortField) => {
    let newDir: 'asc' | 'desc' | null;
    if (sortField === field) {
      if (sortDirection === 'asc') { newDir = 'desc'; setSortDirection('desc'); }
      else if (sortDirection === 'desc') { newDir = null; setSortDirection(null); setSortField('dueDate'); }
      else { newDir = 'asc'; setSortDirection('asc'); }
    } else {
      newDir = 'asc'; setSortField(field); setSortDirection('asc');
    }
    posthog.capture("table_sorted", { table: "sales_receivables", column: field, direction: newDir });
  };

  const sortedData = useMemo(() => {
    if (!sortDirection) return invoices;
    return [...invoices].sort((a, b) => {
      let aV: string | number | null | undefined, bV: string | number | null | undefined;
      switch (sortField) {
        case 'invoiceNumber': aV = a.invoiceNumber; bV = b.invoiceNumber; break;
        case 'customer': aV = a.customerName; bV = b.customerName; break;
        case 'issueDate': aV = new Date(a.issueDate).getTime(); bV = new Date(b.issueDate).getTime(); break;
        case 'dueDate': aV = new Date(a.dueDate).getTime(); bV = new Date(b.dueDate).getTime(); break;
        case 'status': aV = a.status; bV = b.status; break;
        case 'amount': aV = a.totalAmount; bV = b.totalAmount; break;
        case 'balanceDue': aV = a.balance; bV = b.balance; break;
        default: return 0;
      }
      if (aV < bV) return sortDirection === 'asc' ? -1 : 1;
      if (aV > bV) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [invoices, sortField, sortDirection]);

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field;
    return (
      <div onClick={() => handleSort(field)} className="flex items-center gap-1 cursor-pointer hover:text-foreground/80">
        {children}
        {isActive && sortDirection === 'asc' && <ArrowUp className="h-3 w-3" />}
        {isActive && sortDirection === 'desc' && <ArrowDown className="h-3 w-3" />}
        {!isActive && <ArrowUpDown className="h-3 w-3 opacity-30" />}
      </div>
    );
  };

  return (
    <Card ref={visibilityRef} className="m-6 shadow-md">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>Sales & Receivables</CardTitle>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button disabled={isExporting}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/70 active:scale-95 transition-all text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-40">
                  <Download className="h-4 w-4" />
                  <ChevronDown className="h-3.5 w-3.5 opacity-70" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={handleExportCsv} className="gap-2 cursor-pointer">
                  <Download className="h-4 w-4" /> {isExporting ? 'Exporting…' : 'Export to CSV'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DateFilter
              value={dateFilterKey}
              customFrom={dateCustomFrom}
              customTo={dateCustomTo}
              onChange={onDateFilterChange}
              label=""
              width="w-[150px]"
              showEmpty={false}
              pickerSide="left"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter Bar */}
        <div className="mb-4 flex items-center gap-0 px-2.5 py-1.5 bg-muted/20 rounded-lg border">
          {/* Scrollable pills area */}
          <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0 pr-2">
          <Filter className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />

          {/* Company (view mode only) */}
          {activeViewId && (
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <button className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 w-[150px] ${selectedCompanies.length > 0 ? 'bg-muted/70' : 'bg-background'}`}>
                    <span className="text-muted-foreground shrink-0">Company:</span>
                    <span className="font-medium flex-1 truncate text-left">
                      {selectedCompanies.length === 0 ? 'All' : selectedCompanies.length === 1
                        ? uniqueOrganizations.find(o => o.id === selectedCompanies[0])?.name ?? '1 selected'
                        : `${selectedCompanies.length} selected`}
                    </span>
                    <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-1 max-h-60 overflow-y-auto" align="start">
                  {uniqueOrganizations.map(({ id, name }) => (
                    <button key={id}
                      onClick={() => handleCompanyToggle(id)}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/80 text-left">
                      <Check className={`h-3.5 w-3.5 shrink-0 ${selectedCompanies.includes(id) ? 'opacity-100' : 'opacity-0'}`} />
                      <span className="truncate">{name}</span>
                    </button>
                  ))}
                  {uniqueOrganizations.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">No companies loaded</p>}
                </PopoverContent>
              </Popover>
              <div className="h-4 w-px bg-border shrink-0" />
            </>
          )}

          {/* Customer */}
          <Popover>
            <PopoverTrigger asChild>
              <button className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 w-[150px] ${selectedCustomers.length > 0 ? 'bg-muted/70' : 'bg-background'}`}>
                <span className="text-muted-foreground shrink-0">Customer:</span>
                <span className="font-medium flex-1 truncate text-left">
                  {selectedCustomers.length === 0 ? 'All' : selectedCustomers.length === 1 ? selectedCustomers[0] : `${selectedCustomers.length} selected`}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-1 max-h-60 overflow-y-auto" align="start">
              {uniqueCustomers.map(({ name }) => (
                <button key={name}
                  onClick={() => handleCustomerToggle(name)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/80 text-left">
                  <Check className={`h-3.5 w-3.5 shrink-0 ${selectedCustomers.includes(name) ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="truncate">{name}</span>
                </button>
              ))}
              {uniqueCustomers.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">No customers loaded</p>}
            </PopoverContent>
          </Popover>

          <div className="h-4 w-px bg-border shrink-0" />

          {/* Status */}
          <Popover>
            <PopoverTrigger asChild>
              <button className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 w-[130px] ${selectedStatuses.length > 0 ? 'bg-muted/70' : 'bg-background'}`}>
                <span className="text-muted-foreground shrink-0">Status:</span>
                <span className="font-medium flex-1 truncate text-left">
                  {selectedStatuses.length === 0 ? 'All' : selectedStatuses.length === 1
                    ? selectedStatuses[0].replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                    : `${selectedStatuses.length} selected`}
                </span>
                <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-[180px] p-1" align="start">
              {statuses.map((s) => (
                <button key={s}
                  onClick={() => handleStatusToggle(s)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted/80 text-left">
                  <Check className={`h-3.5 w-3.5 shrink-0 ${selectedStatuses.includes(s) ? 'opacity-100' : 'opacity-0'}`} />
                  {s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          <div className="h-4 w-px bg-border shrink-0" />

          {/* Amount range */}
          <div className={`flex items-center gap-1 text-xs h-7 px-2 rounded border border-input shrink-0 transition-colors ${(amountMin || amountMax) ? 'bg-muted/70' : 'bg-background'}`}>
            <span className="text-muted-foreground">$</span>
            <input type="number" min="0" placeholder="Min" value={amountMin} onChange={(e) => setAmountMin(e.target.value)}
              className="w-14 bg-transparent outline-none text-xs" />
            <span className="text-muted-foreground/50">–</span>
            <input type="number" min="0" placeholder="Max" value={amountMax} onChange={(e) => setAmountMax(e.target.value)}
              className="w-14 bg-transparent outline-none text-xs" />
          </div>

          <div className="h-4 w-px bg-border shrink-0" />

          {/* Overdue */}
          <label className={`flex items-center gap-1.5 cursor-pointer select-none text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 whitespace-nowrap ${overdueOnly ? 'bg-muted/70' : 'bg-background'}`}>
            <input type="checkbox" checked={overdueOnly} onChange={(e) => handleOverdueToggle(e.target.checked)}
              className="h-3 w-3 rounded accent-primary" />
            <span>Overdue only</span>
          </label>

          {/* Due Soon */}
          <label className={`flex items-center gap-1.5 cursor-pointer select-none text-xs h-7 px-2 rounded border border-input hover:bg-muted/80 transition-colors shrink-0 whitespace-nowrap ${dueSoonOnly ? 'bg-muted/70' : 'bg-background'}`}>
            <input type="checkbox" checked={dueSoonOnly} onChange={(e) => handleDueSoonToggle(e.target.checked)}
              className="h-3 w-3 rounded accent-primary" />
            <span>Due soon</span>
          </label>

          </div>
          {/* Always-visible action buttons */}
          <div className="flex items-center gap-1.5 shrink-0 pl-2 border-l border-border">
            <Button variant="default" size="sm" onClick={handleApplyFilters}
              className="h-7 text-xs px-2.5 gap-1" disabled={!hasFilterChanges || isLoadingData}>
              <Filter className="h-3 w-3" /> Apply
            </Button>
            <Button variant="outline" size="sm" onClick={clearFilters}
              className="h-7 text-xs px-2.5 gap-1" disabled={!hasActiveFilters || isLoadingData}>
              <X className="h-3 w-3" /> Reset
            </Button>
          </div>
        </div>

        {/* KPI Summary — 2 grouped cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          <KpiGroupCard items={[
            { label: "Total Billed", value: formatAmount(kpis.totalBilled, 'USD'), compactValue: formatAmountCompact(kpis.totalBilled), sub: `${kpis.totalBilledCount} invoice${kpis.totalBilledCount !== 1 ? 's' : ''}`, valueClass: 'text-sky-600', period: periodLabel, tip: "Total invoiced amount for non-void invoices issued in the selected period." },
            { label: "Account Receivables", value: formatAmount(kpis.openBalance, 'USD'), compactValue: formatAmountCompact(kpis.openBalance), sub: `${kpis.openBalanceCount} unpaid invoice${kpis.openBalanceCount !== 1 ? 's' : ''}`, valueClass: '', period: periodLabel, tip: "Outstanding AR as of the end of the selected period, sourced from QuickBooks." },
            { label: "Overdue", value: formatAmount(kpis.overdueAmount, 'USD'), compactValue: formatAmountCompact(kpis.overdueAmount), sub: `${kpis.overdueCount} invoice${kpis.overdueCount !== 1 ? 's' : ''} past due`, valueClass: 'text-red-600', tip: "Sum of remaining balances for invoices issued in the selected period whose due date has passed and are still unpaid.", onClick: () => applyKpiQuickFilter(['unpaid', 'partially_paid'], true) },
            { label: "Upcoming Due This Week", value: formatAmount(kpis.dueSoonAmount, 'USD'), compactValue: formatAmountCompact(kpis.dueSoonAmount), sub: `${kpis.dueSoonCount} due this week`, valueClass: 'text-amber-600', live: true, tip: "Sum of remaining balances for unpaid invoices (issued in the selected period) with a due date in the next 7 days.", onClick: () => applyKpiQuickFilter(['unpaid', 'partially_paid'], false, true) },
          ]} />
          <KpiGroupCard items={[
            { label: "Cash Collected", value: formatAmount(kpis.cashCollected, 'USD'), compactValue: formatAmountCompact(kpis.cashCollected), sub: "Paid invoices", valueClass: 'text-emerald-600', period: periodLabel, tip: "Total amount received from paid invoices in the selected period." },
            { label: "Collection Rate", value: kpis.collectionRate !== null ? `${kpis.collectionRate.toFixed(1)}%` : '—', sub: "Payments collected ÷ invoices due", valueClass: 'text-sky-600', period: periodLabel, tip: "(Payments Collected ÷ Invoices Due) × 100 · Invoices Due = non-void invoices with a due date in the selected period." },
            { label: "Avg Days to Collect", value: kpis.avgDaysToCollect !== null && kpis.avgDaysCount >= 1 ? `${Math.round(kpis.avgDaysToCollect)} days` : '—', sub: kpis.avgDaysCount > 0 ? `Based on ${kpis.avgDaysCount} payment${kpis.avgDaysCount !== 1 ? 's' : ''}${kpis.avgDaysCount < 3 ? ' — low confidence' : ''}` : 'No payments recorded', valueClass: kpis.avgDaysCount >= 3 ? (kpis.avgDaysToCollect! > 45 ? 'text-red-600' : kpis.avgDaysToCollect! > 30 ? 'text-amber-600' : 'text-emerald-600') : '', period: periodLabel, tip: "Weighted average days from invoice issue to payment · Σ(amount × days) / Σ(amount) · only payments in the selected period · filters by selected customer when applied." },
          ]} />
        </div>

        {/* Main Data Grid */}
        <div className="border rounded-lg overflow-x-auto" dir="ltr">
          <div style={{ minWidth: `${totalWidth + (colWidths.length - 1) * 16 + 24}px` }}>
          <div className="grid gap-4 p-3 bg-muted/30 border-b font-semibold text-sm [&>*]:min-w-0" style={{ gridTemplateColumns: gridTemplate }}>
            <div className="relative group overflow-hidden">
              <SortableHeader field="invoiceNumber">Invoice #</SortableHeader>
              <ResizeHandle onMouseDown={(e) => startResize(0, e)} className="opacity-0 group-hover:opacity-100" />
            </div>
            {activeViewId && (
              <div className="relative group overflow-hidden">
                <div>Company</div>
                <ResizeHandle onMouseDown={(e) => startResize(1, e)} className="opacity-0 group-hover:opacity-100" />
              </div>
            )}
            <div className="relative group overflow-hidden">
              <SortableHeader field="customer">Customer</SortableHeader>
              <ResizeHandle onMouseDown={(e) => startResize(companyColOffset + 1, e)} className="opacity-0 group-hover:opacity-100" />
            </div>
            <div className="relative group overflow-hidden">
              <SortableHeader field="issueDate">Date Issued</SortableHeader>
              <ResizeHandle onMouseDown={(e) => startResize(companyColOffset + 2, e)} className="opacity-0 group-hover:opacity-100" />
            </div>
            <div className="relative group overflow-hidden">
              <SortableHeader field="dueDate">Due Date</SortableHeader>
              <ResizeHandle onMouseDown={(e) => startResize(companyColOffset + 3, e)} className="opacity-0 group-hover:opacity-100" />
            </div>
            <div className="relative group overflow-hidden">
              <SortableHeader field="status">Status</SortableHeader>
              <ResizeHandle onMouseDown={(e) => startResize(companyColOffset + 4, e)} className="opacity-0 group-hover:opacity-100" />
            </div>
            <div className="relative group overflow-hidden">
              <SortableHeader field="amount">Amount</SortableHeader>
              <ResizeHandle onMouseDown={(e) => startResize(companyColOffset + 5, e)} className="opacity-0 group-hover:opacity-100" />
            </div>
            <div className="relative group overflow-hidden">
              <div>Paid</div>
              <ResizeHandle onMouseDown={(e) => startResize(companyColOffset + 6, e)} className="opacity-0 group-hover:opacity-100" />
            </div>
            <div className="relative group overflow-hidden">
              <SortableHeader field="balanceDue">Balance Due</SortableHeader>
              <ResizeHandle onMouseDown={(e) => startResize(companyColOffset + 7, e)} className="opacity-0 group-hover:opacity-100" />
            </div>
            <div>Actions</div>
          </div>

          {sortedData.map((invoice) => (
            <div key={invoice.id} className="group">
              <div
                onClick={() => setExpandedInvoiceId(expandedInvoiceId === invoice.id ? null : invoice.id)}
                className={`grid gap-4 p-3 cursor-pointer hover:bg-muted/10 transition-colors border-b [&>*]:min-w-0 ${expandedInvoiceId === invoice.id ? 'bg-muted/5' : ''}`}
                style={{ gridTemplateColumns: gridTemplate }}
              >
                <TruncatedCell content={invoice.invoiceNumber} className="text-sm" />
                {activeViewId && <TruncatedCell content={invoice.organizationName ?? '—'} className="text-sm text-muted-foreground" />}
                <TruncatedCell content={invoice.customerName} className="text-sm" />
                <div className="text-sm">{formatDate(invoice.issueDate)}</div>
                <div className="text-sm">{formatDate(invoice.dueDate)}</div>
                <div><StatusBadge status={invoice.status} /></div>
                <div className="text-sm">{formatAmount(invoice.totalAmount, invoice.currency)}</div>
                <div className={`text-sm ${invoice.totalAmount - invoice.balance > 0 ? 'text-emerald-600' : ''}`}>{formatAmount(invoice.totalAmount - invoice.balance, invoice.currency)}</div>
                <div className="text-sm">{formatAmount(invoice.balance, invoice.currency)}</div>
                <div onClick={(e) => e.stopPropagation()}>
                  <ActionsCell
                    invoice={invoice}
                    onAskChat={onAskChat}
                    onReminder={() => setReminderModal({
                      invoiceId: invoice.id,
                      label: `Invoice ${invoice.invoiceNumber || invoice.id} — ${invoice.customerName}`,
                    })}
                    onComment={() => setCommentsTarget({
                      referenceType: "invoice",
                      referenceId: invoice.id,
                      label: `Invoice ${invoice.invoiceNumber || invoice.id} — ${invoice.customerName}`,
                    })}
                    rowReminder={rowReminders.get(invoice.id)}
                    commentCount={commentCounts.get(invoice.id)}
                  />
                </div>
              </div>

              {expandedInvoiceId === invoice.id && (
                <div className="border-b bg-muted/5">
                  <PaymentDetails
                    invoiceId={invoice.id}
                    status={invoice.status}
                    onPaymentClick={(p) => { setSelectedPayment(p); setPaymentSheetOpen(true); }}
                  />
                </div>
              )}
            </div>
          ))}

          {isLoadingData ? (
            <div className="p-8 text-center text-muted-foreground">
              <div className="flex flex-col items-center gap-3">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-sm">Loading invoices…</span>
              </div>
            </div>
          ) : sortedData.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">No invoices found</div>
          )}
          </div>
        </div>

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between border-t pt-4">
            <div className="text-sm text-muted-foreground">
              Showing {((currentPage - 1) * pagination.limit) + 1} – {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} invoices
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1 || isLoadingData} className="gap-1">
                <ChevronLeft className="h-4 w-4" /> Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= pagination.totalPages - 2) pageNum = pagination.totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;
                  return (
                    <Button key={pageNum} variant={pageNum === currentPage ? "default" : "outline"} size="sm"
                      onClick={() => handlePageChange(pageNum)} disabled={isLoadingData} className="w-8 h-8 p-0">
                      {pageNum}
                    </Button>
                  );
                })}
              </div>
              <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= pagination.totalPages || isLoadingData} className="gap-1">
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>

      {/* Payment detail sheet */}
      <PaymentDetailSheet
        payment={selectedPayment}
        open={paymentSheetOpen}
        onClose={() => setPaymentSheetOpen(false)}
      />

      {/* Reminder creation modal */}
      {reminderModal && (
        <ReminderCreateModal
          open={!!reminderModal}
          onClose={() => setReminderModal(null)}
          onCreated={(targetNames, text) => {
            if (reminderModal) {
              setRowReminders(prev => new Map(prev).set(reminderModal.invoiceId, { text, targetNames }));
            }
          }}
          entityType="invoice"
          entityId={reminderModal.invoiceId}
          entityLabel={reminderModal.label}
        />
      )}

      {/* Comments sheet */}
      <CommentsSheet
        target={commentsTarget}
        currentUserId={currentUserId}
        role={role}
        onClose={() => setCommentsTarget(null)}
        onCommentCountChange={(referenceId, count) =>
          setCommentCounts((prev) => new Map(prev).set(referenceId, count))
        }
      />
    </Card>
  );
};

export default SalesReceivables;
